import { EndpointUrl } from 'api/endpoint';
import { useFetch, useFetcher } from 'api/fetcher';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import {
  FinsightEntity,
  FinsightEntityCreateRequest,
  FinsightEntityCreateRequestSchema,
  FinsightEntityField,
  FinsightEntityUpdateRequest,
  FinsightEntityUpdateRequestSchema,
  SchemaConstraints,
} from 'shared';
import useSWR, { useSWRConfig } from 'swr';

// Hook to search entities
export function useEntitySearch(query: string | null, type?: string) {
  const { isValid, workspaceId, fetcher } = useFetch<FinsightEntity[]>('POST', {
    query,
    type,
  });
  return useSWR(
    isValid && query
      ? { url: `${EndpointUrl}/workspace/${workspaceId}/search`, query }
      : null,
    (data) => fetcher(data.url),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );
}

export function useEntityFetcher<T = FinsightEntity[]>(urlPart: string | null) {
  const response = useFetcher<T>(urlPart);
  if (
    response.data &&
    typeof response.data === 'object' &&
    'error' in response.data
  ) {
    return {
      ...response,
      data: undefined,
      error: response.data.error,
    };
  }

  return response;
}

export function useAllEntities(type: string) {
  return useEntityFetcher(`entities/${type}/all`);
}

export function useRootEntities(type: string) {
  return useEntityFetcher(`entities/${type}/root`);
}

export function useRecentEntities(type: string) {
  return useEntityFetcher(`entities/${type}/recent`);
}

export function useEntityTypes() {
  return useFetcher<string[]>(`entity-types`);
}

export function useEntity(id: string | undefined) {
  return useEntityFetcher<FinsightEntity | null>(id ? `entity/${id}` : null);
}

export function useEntityChildren(id: string | undefined, all = false) {
  return useEntityFetcher(id ? `entity/${id}/children?all=${all}` : null);
}

export function useEntityExtraFields(type?: string) {
  return useEntityFetcher<FinsightEntityField[]>(
    type ? `entities/${type}/fields` : null,
  );
}

export function useEntityQuery(filters: SchemaConstraints[] | null) {
  const { isValid, fetcher, workspaceId } = useFetch<FinsightEntity[]>(
    'POST',
    filters,
  );

  return useSWR(
    isValid && filters
      ? `${EndpointUrl}/workspace/${workspaceId}/entities/all`
      : null,
    fetcher,
  );
}

export function useEntityActions() {
  const workspaceId = useWorkspaceId();

  const { cache, mutate } = useSWRConfig();

  return {
    createEntity: async function (entityData: FinsightEntityCreateRequest) {
      if (!workspaceId) throw new Error('No workspace selected');
      const payload = FinsightEntityCreateRequestSchema.parse(entityData);
      return await fetch(`${EndpointUrl}/workspace/${workspaceId}/entity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-finsight-token': localStorage.getItem('token') || '',
        },
        body: JSON.stringify(payload),
      });
    },
    deleteEntity: async function (id: string) {
      if (!workspaceId) throw new Error('No workspace selected');
      return await fetch(
        `${EndpointUrl}/workspace/${workspaceId}/entity/${id}`,
        {
          method: 'DELETE',
          headers: {
            'x-finsight-token': localStorage.getItem('token') || '',
          },
        },
      );
    },
    batchDeleteEntities: async function (entityIds: string[]) {
      if (!workspaceId) throw new Error('No workspace selected');
      return await fetch(
        `${EndpointUrl}/workspace/${workspaceId}/entities/batch-delete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-finsight-token': localStorage.getItem('token') || '',
          },
          body: JSON.stringify({ entityIds }),
        },
      );
    },
    updateEntity: async function (
      id: string,
      entityData: FinsightEntityUpdateRequest,
      fullEntity?: FinsightEntity,
    ) {
      if (!workspaceId) throw new Error('No workspace selected');
      const payload = FinsightEntityUpdateRequestSchema.parse(entityData);
      const response = await fetch(
        `${EndpointUrl}/workspace/${workspaceId}/entity/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-finsight-token': localStorage.getItem('token') || '',
          },
          body: JSON.stringify(payload),
        },
      );

      mutate(`${EndpointUrl}/workspace/${workspaceId}/entity/${id}`);

      if (fullEntity) {
        if (fullEntity.parent) {
          Array.from(cache.keys())
            .filter((x) =>
              x.includes(
                `/workspace/${workspaceId}/entity/${fullEntity.parent}`,
              ),
            )
            .forEach((key) => mutate(key));
        } else {
          Array.from(cache.keys())
            .filter((x) =>
              x.includes(
                `/workspace/${workspaceId}/entities/${fullEntity.types[0]}/root`,
              ),
            )
            .forEach((key) => mutate(key));
        }
      }

      if (entityData.parent) {
        Array.from(cache.keys())
          .filter((x) =>
            x.includes(`/workspace/${workspaceId}/entity/${entityData.parent}`),
          )
          .forEach((key) => mutate(key));
      }

      return response;
    },
  };
}
